import { Formik, Form } from "formik";
import { queryClient } from "../../queryClient";
import { showError } from "../../utils/error-handling";
import { notify } from "../../utils/notify";
import { Box } from "../Box";
import { Button } from "../Button";
import { Flex } from "../Flex";
import { FormRow } from "../Form";
import { Input, InputProps } from "../Input";
import { Loader } from "../Loader";
import { Modal } from "../Modal";
import { Paragraph } from "../Paragraph";
import { addEditConsultantValidation } from "../../utils/validationSchema";
import { useUpdateConsultantDetails } from "../../data/useUpdateConsultantDetails";
import { useCreateConsultant } from "../../data/useCreateConsultant";
import { useConsultantDetailById } from "../../data/useConsultantDetailById";

export const AddEditConsultantsModal = (props: any) => {
    const { isOpen, onRequestClose, consultantId } = props;
    const mode = typeof consultantId === "number" ? "edit" : "create";

    const { mutate: createConsultant, isLoading: isLoadingCreate } =
    useCreateConsultant();
    const { mutate: updateConsultant, isLoading: isLoadingUpdate } =
        useUpdateConsultantDetails();
    const { data: consultant, isLoading: isLoadingConsultantDetails } = useConsultantDetailById(
        consultantId,
        mode === "edit"
    );

    const isLoadingConsultant = mode === "edit" ? isLoadingConsultantDetails : false;
    const isLoadingAction = isLoadingCreate || isLoadingUpdate;

    const handleSubmit = (data) => {
        const payload: any = {
            name: data.name,
            email: data.email,
        };
        const onComplete = () => {
            queryClient.invalidateQueries(["consultant"]);
            onRequestClose();
        };

        if (mode === "edit") {
            updateConsultant(
                {
                    id: consultantId,
                    ...payload,
                },
                {
                    onSuccess: (res) => {
                        if (res?.status?.toString().startsWith("2"))
                            notify("Consultant Updated", {
                                type: "success",
                            });
                        onComplete();
                        queryClient.invalidateQueries(["consultantDetails", consultantId]);
                    },
                    onError: (err: any) => {
                        showError(err, "A problem occurred while updating the consultant.");
                    },
                }
            );
        } else {
            createConsultant(payload, {
                onSuccess: (res) => {
                    if (res?.status?.toString().startsWith("2"))
                        notify("Consultant Invited", {
                            type: "success",
                        });
                    onComplete();
                },
                onError: (err: any) => {
                    showError(err, "A problem occurred while creating the consultant.");
                },
            });
        }
    };

    const inputProps: Partial<InputProps> = {
        minWidth: 200,
        flex: 1,
    };

    return (
        <Modal width="400px" isOpen={isOpen} onClose={onRequestClose}>
            {isLoadingConsultant && (
                <Flex justifyContent="center" alignItems="center" minHeight="25vh">
                    <Loader />
                </Flex>
            )}

            {!isLoadingConsultant && (
                <Formik
                    initialValues={{
                        name: consultant?.data.name || "",
                        email: consultant?.data.email || "",
                    }}
                    validationSchema={addEditConsultantValidation}
                    onSubmit={(values) => {
                        handleSubmit(values);
                    }}
                >
                    {(props) => (
                        <Form onSubmit={props.handleSubmit}>
                            <Box>
                                <Paragraph fontWeight="bold" fontSize={3}>
                                    {consultantId ? "Edit Consultants" : "Create Consultants"}
                                </Paragraph>
                                <FormRow my={3}>
                                    <Input
                                        onChange={props.handleChange}
                                        value={props.values.name}
                                        placeholder="Enter your name"
                                        label="Name"
                                        name="name"
                                        {...inputProps}
                                        isRequired
                                        hasError={Boolean(props.errors.name)}
                                        errorMessage={props.errors.name}
                                    />
                                </FormRow>
                                <FormRow my={3}>
                                    <Input
                                        onChange={props.handleChange}
                                        value={props.values.email}
                                        placeholder="Enter your email address"
                                        label="Email Address"
                                        name="email"
                                        {...inputProps}
                                        isRequired
                                        hasError={Boolean(props.errors.email)}
                                        errorMessage={props.errors.email}
                                    />
                                </FormRow>
                            </Box>
                            <Flex justifyContent={"end"}>
                                <Box>
                                    <Button
                                        type="submit"
                                        mt="4"
                                        mb="0"
                                        isDisabled={
                                            !props.dirty ||
                                            (props.dirty && !props.isValid) ||
                                            isLoadingAction
                                        }
                                    >
                                        {"Confirm"}
                                    </Button>
                                </Box>
                            </Flex>
                        </Form>
                    )}
                </Formik>
            )}
        </Modal>
    );
};
